import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { getGeocode, getLatLng } from "use-places-autocomplete";

import { StoresListFilters } from "../../../../contexts/store-locator";
import { useDataLayer } from "../../../../hooks/use-data-layer";
import { useStoreLocator } from "../../../../hooks/use-store-locator";
import { CompanySiteType } from "../../../../types/company-site";
import { GoogleAnalyticsEvent } from "../../../../utils/analytics";
import { Checkbox, FormFieldLabel, TextInput } from "../../../forms";
import { Icon } from "../../../icon";
import { StoreIcon } from "../store-icon";

export interface StoresListHeaderProps {
  labels: {
    "store-locator-title": string;
    "store-locator-results-count": string;
    "store-locator-search-placeholder": string;
    "store-locator-search-company-type-label": string;
    "company-site-types-biesse-venue": string;
    "company-site-types-production-site": string;
    "company-site-types-dealer": string;
    "company-site-types-biesse": string;
  };
  className?: string;
}

export function StoresListHeader({ labels, className }: StoresListHeaderProps) {
  const { filteredStores, setSearchValue, searchValue, suggestions } = useStoreLocator();
  const { setValue, watch } = useFormContext<StoresListFilters>();
  const filteredTypes = watch("types");
  const [areFiltersOpen, setFiltersOpen] = useState(false);
  const { pushEvent } = useDataLayer();

  const formattedTypes: Record<CompanySiteType | "biesse", string> = {
    biesse: labels["company-site-types-biesse"],
    [CompanySiteType.BiesseVenue]: labels["company-site-types-biesse"],
    [CompanySiteType.ProductionSite]: labels["company-site-types-production-site"],
    [CompanySiteType.Dealer]: labels["company-site-types-dealer"],
  };

  const setCurrentCoordinates = useCallback(
    (updateSearch = true) => {
      try {
        navigator.geolocation.getCurrentPosition(
          async (geoLocation) => {
            const { latitude, longitude } = geoLocation.coords;
            setValue("latitude", latitude);
            setValue("longitude", longitude);

            const results = await getGeocode({ location: { lat: latitude, lng: longitude } });
            const place = results?.[0];
            if (place && updateSearch) {
              setSearchValue(place.formatted_address, false);
            }
          },
          () => {
            setValue("latitude", null);
            setValue("longitude", null);
          }
        );
      } catch (err) {
        console.error(err);
      }
    },
    [setValue, setSearchValue]
  );

  const setCoordinatesForAddress = useCallback(
    (address: string) => {
      pushEvent({
        event: GoogleAnalyticsEvent.Locator,
        location: address,
        locator_type: Array.from(filteredTypes)
          .map((x) => x.toLowerCase())
          .join(" | "),
      });
      getGeocode({ address })
        .then((results) => {
          const { lat, lng } = getLatLng(results[0]);
          setValue("latitude", lat);
          setValue("longitude", lng);
        })
        .catch((e) => {
          if (e !== "ZERO_RESULTS") {
            console.error(e);
          }
        });
    },
    [setValue, pushEvent, filteredTypes]
  );

  const hasSetCoordinatesFirstTime = useRef(false);
  useEffect(() => {
    if (!hasSetCoordinatesFirstTime.current) {
      setCurrentCoordinates();
      hasSetCoordinatesFirstTime.current = true;
    }
  }, [setCurrentCoordinates]);

  return (
    <div className={clsx("lg:bg-neutral-light lg:rounded-xl flex flex-col gap-2", className)}>
      <div className="px-3 pt-3 flex flex-col gap-2 pr-5">
        <h3 className="text-xl text-primary">{labels["store-locator-title"]}</h3>

        <TextInput
          placeholder={labels["store-locator-search-placeholder"]}
          endDecoration="crosshair"
          onClickDecoration={() => setCurrentCoordinates()}
          value={searchValue}
          onChangeValue={(text, option) => {
            setSearchValue(text, !option);
            if (option) {
              setCoordinatesForAddress(option.label);
            }
          }}
          inputProps={{
            onKeyDown: (e) => {
              if (e.key === "Enter") {
                setCoordinatesForAddress(searchValue);
              }
            },
          }}
          options={suggestions.map((item) => ({ label: item.description, value: item.place_id }))}
        />

        <div className="flex justify-between w-full">
          <p className="font-light text-sm">
            {labels["store-locator-results-count"].replace(
              "{{count}}",
              filteredStores.length.toString()
            )}
          </p>
          <button type="button" onClick={() => setFiltersOpen((v) => !v)}>
            <Icon icon="filter" size={24} />
          </button>
        </div>
        <div
          className={clsx(
            "overflow-hidden transition-all duration-300",
            areFiltersOpen ? "max-h-screen" : "max-h-0"
          )}
        >
          <FormFieldLabel label={labels["store-locator-search-company-type-label"]} />
          {[CompanySiteType.BiesseVenue, CompanySiteType.Dealer].map((type, i) => (
            <Checkbox
              key={i}
              collapseMessage
              checked={filteredTypes.has(type)}
              onChange={() => {
                const newTypes = new Set(filteredTypes);
                if (!filteredTypes.has(type)) {
                  newTypes.add(type);
                  if (type === CompanySiteType.BiesseVenue) {
                    newTypes.add(CompanySiteType.ProductionSite);
                  }
                } else {
                  newTypes.delete(type);
                  if (type === CompanySiteType.BiesseVenue) {
                    newTypes.delete(CompanySiteType.ProductionSite);
                  }
                }
                setValue("types", newTypes);
              }}
              className="flex items-center"
            >
              <span className="pr-2">
                {type === CompanySiteType.BiesseVenue
                  ? formattedTypes.biesse
                  : formattedTypes[type]}
              </span>{" "}
              <StoreIcon type={type} size={28} />
            </Checkbox>
          ))}
        </div>
      </div>
    </div>
  );
}
